import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { History, Loader2, X } from 'lucide-react';

import { useT, type StringKey } from '@/i18n';
import { cn } from '@/lib/utils';
import { ApiError } from '@/lib/api-client';
import { toast } from '@/stores/toast.store';
import { usersApi } from '@/features/users/users.api';
import {
  HISTORY_TRACKED_KEYS,
  systemConfigApi,
  type SystemConfigItem,
} from '@/features/system-config/system-config.api';
import { useSystemConfigList } from '@/features/system-config/system-config.hooks';
import {
  CATALOG,
  FROZEN_FACTS,
  FOLDED_DAILY_KEY,
  GROUP_ORDER,
  UNIQUE_DAILY_KEY,
  catalogByKey,
  keysInGroup,
  visibleKeyCount,
  type CatalogKey,
  type ConfigGroupId,
  type FieldKind,
} from '@/features/system-config/admin-config-catalog';
import { ThresholdEditModal } from './components/ThresholdEditModal';
import { TierThresholdsHistoryModal } from './components/TierThresholdsHistoryModal';

const GROUP_PARAM = new Set<ConfigGroupId>(GROUP_ORDER);

const UNIT_I18N: Record<Extract<FieldKind, { kind: 'int' }>['unit'], StringKey> = {
  minutes: 'f5t1a.unit.minutes',
  seconds: 'f5t1a.unit.seconds',
  attempts: 'f5t1a.unit.attempts',
  days: 'f5t1a.unit.days',
  years: 'f5t1a.unit.years',
  points: 'f5t1a.unit.points',
  hours: 'f5t1a.unit.hours',
  screenings: 'f5t1a.unit.screenings',
  uploads: 'f5t1a.unit.uploads',
  units: 'f5t1a.unit.units',
  followups: 'f5t1a.unit.followups',
  dayOfMonth: 'f5t1a.unit.dayOfMonth',
  weight: 'f5t1a.unit.weight',
};

const GROUP_NAME: Record<ConfigGroupId, StringKey> = {
  operacion: 'f5t1a.group.operacion',
  metas: 'f5t1a.group.metas',
  nucleo: 'f5t1a.group.nucleo',
  navegacion: 'f5t1a.group.navegacion',
  congeladas: 'f5t1a.group.congeladas',
};

const GROUP_NOTE: Record<ConfigGroupId, StringKey> = {
  operacion: 'f5t1a.group.operacion.note',
  metas: 'f5t1a.group.metas.note',
  nucleo: 'f5t1a.group.nucleo.note',
  navegacion: 'f5t1a.group.navegacion.note',
  congeladas: 'f5t1a.group.congeladas.note',
};

const GROUP_WARN: Record<ConfigGroupId, StringKey | null> = {
  operacion: null,
  metas: 'f5t1a.group.metas.warn',
  nucleo: null,
  navegacion: 'f5t1a.group.navegacion.warn',
  congeladas: null,
};

function labelKeyFor(key: string): StringKey {
  const k = `f5t1a.key.${key}` as StringKey;
  return k;
}

function descKeyFor(key: string): StringKey {
  return `f5t1a.desc.${key}` as StringKey;
}

/**
 * Admin 08 — Configuración (F5-T1a). Superficie + consolidación, sin
 * migración. Tokens del brand en ESTA ventana (primera admin del reskin).
 * El shell admin sigue Inter (T3).
 */
export function AdminConfigPage(): JSX.Element {
  const t = useT();
  const [params, setParams] = useSearchParams();
  const requested = params.get('group');
  const group: ConfigGroupId = GROUP_PARAM.has(requested as ConfigGroupId)
    ? (requested as ConfigGroupId)
    : 'metas';
  const setGroup = (next: ConfigGroupId): void => {
    const n = new URLSearchParams(params);
    if (next === 'metas') n.delete('group');
    else n.set('group', next);
    setParams(n, { replace: true });
  };

  const { data, isLoading, error } = useSystemConfigList();
  const byKey = useMemo(() => {
    const m = new Map<string, SystemConfigItem>();
    for (const it of data?.items ?? []) m.set(it.key, it);
    return m;
  }, [data]);

  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [historyKey, setHistoryKey] = useState<string | null>(null);

  const editingSpec = editingKey ? catalogByKey.get(editingKey) ?? null : null;
  const editingItem = editingKey ? byKey.get(editingKey) ?? null : null;
  const usePayrollModal = !!(editingSpec?.payroll && editingItem && HISTORY_TRACKED_KEYS.has(editingItem.key));

  const dailyLive = byKey.get(UNIQUE_DAILY_KEY)?.value;
  const foldedLive = byKey.get(FOLDED_DAILY_KEY)?.value;
  const dailyDiverge = dailyLive != null && foldedLive != null && String(dailyLive) !== String(foldedLive);

  const uncatalogued = (data?.items ?? []).filter((it) => !catalogByKey.has(it.key));

  return (
    <div
      data-testid="f5-t1a-page"
      className="font-body text-nav-navy max-w-[1100px]"
    >
      <header className="mb-6">
        <p className="font-head text-[10.5px] font-bold tracking-[0.14em] uppercase text-nav-slate m-0">
          {t('f5t1a.kicker')}
        </p>
        <h1 className="font-head text-[32px] font-extrabold tracking-[-0.025em] leading-tight mt-1 mb-2">
          {t('f5t1a.title')}
        </h1>
        <p className="text-[15px] leading-relaxed text-nav-navy/90 m-0 max-w-[640px]">
          {t('f5t1a.subtitle')}
        </p>
        <p
          data-testid="f5-t1a-t1b-note"
          className="mt-3 text-[13px] leading-relaxed text-nav-slate max-w-[640px] m-0"
        >
          {t('f5t1a.t1bNote')}
        </p>
        <p className="mt-2 text-[13px] text-nav-slate">
          {t('f5t1a.overrideNote')}{' '}
          <Link
            to="/admin/team"
            className="text-nav-tealDark underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2 rounded"
          >
            {t('f5t1a.overrideLink')}
          </Link>
          .
        </p>
      </header>

      <div className="flex gap-6 flex-wrap">
        <nav className="w-[250px] flex-none min-w-[220px]" aria-label={t('f5t1a.groupsAria')}>
          <p className="font-head text-[10.5px] font-bold tracking-[0.14em] uppercase text-nav-slate mb-2.5">
            {t('f5t1a.groupsKicker', { n: CATALOG.length })}
          </p>
          <div className="flex flex-col gap-1">
            {GROUP_ORDER.map((g) => {
              const on = group === g;
              const warn = GROUP_WARN[g];
              return (
                <button
                  key={g}
                  type="button"
                  data-testid={`f5-t1a-group-${g}`}
                  aria-pressed={on}
                  onClick={() => setGroup(g)}
                  className={cn(
                    'flex items-center gap-2.5 w-full box-border min-h-[60px] px-3.5 py-2.5 rounded-lg border-[1.5px] text-left transition-colors duration-150',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2',
                    on
                      ? 'bg-nav-navy border-nav-navy text-white'
                      : 'bg-white border-nav-card text-nav-navy hover:bg-nav-soft hover:border-nav-teal',
                  )}
                >
                  <span className="flex-1 min-w-0">
                    <span className="block font-head text-[13.5px] font-bold">{t(GROUP_NAME[g])}</span>
                    {warn && (
                      <span
                        className={cn(
                          'block mt-0.5 font-head text-[10px] font-bold tracking-[0.08em] uppercase',
                          on ? 'text-[#f0b8b0]' : 'text-nav-red',
                        )}
                      >
                        {t(warn)}
                      </span>
                    )}
                  </span>
                  <span
                    className={cn(
                      'flex-none font-head text-[13px] font-bold tabular-nums',
                      on ? 'text-white/70' : 'text-nav-faint',
                    )}
                  >
                    {visibleKeyCount(g)}
                  </span>
                </button>
              );
            })}
          </div>
        </nav>

        <div className="flex-1 min-w-[320px]">
          {isLoading ? (
            <div className="flex items-center gap-2 text-sm text-nav-slate py-10">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              {t('f5t1a.loading')}
            </div>
          ) : error ? (
            <p className="text-sm text-nav-red py-6">{(error as Error).message}</p>
          ) : (
            <>
              {(GROUP_WARN[group]) && (
                <div
                  data-testid="f5-t1a-muted-banner"
                  className="mb-4 rounded-xl border-[1.5px] border-nav-card bg-nav-soft px-5 py-4 text-[13.5px] leading-relaxed text-nav-slate"
                >
                  {t('f5t1a.mutedBanner')}
                </div>
              )}

              <div className="bg-white border-[1.5px] border-nav-card rounded-xl overflow-hidden">
                <div className="flex items-baseline justify-between gap-4 flex-wrap px-6 pt-5 pb-3.5">
                  <p className="m-0 font-head text-[10.5px] font-bold tracking-[0.14em] uppercase text-nav-teal">
                    {t(GROUP_NAME[group])}
                  </p>
                  <p className="m-0 text-[13px] text-nav-slate">{t(GROUP_NOTE[group])}</p>
                </div>

                {keysInGroup(group).map((spec) => {
                  const item = byKey.get(spec.key);
                  return (
                    <KeyRow
                      key={spec.key}
                      spec={spec}
                      item={item}
                      t={t}
                      dailyDiverge={spec.key === UNIQUE_DAILY_KEY && dailyDiverge}
                      onEdit={() => setEditingKey(spec.key)}
                      onHistory={
                        spec.payroll && HISTORY_TRACKED_KEYS.has(spec.key)
                          ? () => setHistoryKey(spec.key)
                          : undefined
                      }
                    />
                  );
                })}

                {group === 'congeladas' &&
                  FROZEN_FACTS.map((fact) => <FrozenFactRow key={fact.id} factId={fact.id} value={fact.value} t={t} />)}
              </div>

              {group === 'metas' && (
                <div
                  data-testid="f5-t1a-dupes"
                  className="mt-4 bg-nav-tealTint border-[1.5px] border-nav-teal rounded-xl px-5 py-5 text-nav-tealDark"
                >
                  <p className="m-0 mb-3.5 font-head text-[10.5px] font-bold tracking-[0.12em] uppercase">
                    {t('f5t1a.dupes.title')}
                  </p>
                  <div className="grid gap-4 md:grid-cols-2 text-[13.5px] leading-relaxed">
                    <p className="m-0">
                      <b>{t('f5t1a.dupes.dailyTitle')}</b> {t('f5t1a.dupes.dailyBody')}
                    </p>
                    <p className="m-0">
                      <b>{t('f5t1a.dupes.ladderTitle')}</b> {t('f5t1a.dupes.ladderBody')}
                    </p>
                  </div>
                </div>
              )}

              {uncatalogued.length > 0 && group === 'operacion' && (
                <p className="mt-3 text-xs text-nav-slate">
                  {t('f5t1a.uncatalogued', { n: uncatalogued.length })}
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {usePayrollModal && editingItem && (
        <ThresholdEditModal
          open={true}
          configKey={editingItem.key}
          currentValue={editingItem.value}
          onClose={() => setEditingKey(null)}
        />
      )}
      {editingSpec && editingItem && !usePayrollModal && !editingSpec.frozen && (
        <SimpleEditPanel
          spec={editingSpec}
          item={editingItem}
          onClose={() => setEditingKey(null)}
        />
      )}
      {historyKey && (
        <TierThresholdsHistoryModal
          open={true}
          configKey={historyKey}
          onClose={() => setHistoryKey(null)}
        />
      )}
    </div>
  );
}

function KeyRow({
  spec,
  item,
  t,
  dailyDiverge,
  onEdit,
  onHistory,
}: {
  spec: CatalogKey;
  item: SystemConfigItem | undefined;
  t: ReturnType<typeof useT>;
  dailyDiverge: boolean;
  onEdit: () => void;
  onHistory?: () => void;
}): JSX.Element {
  const frozen = spec.frozen || item?.readonly;
  return (
    <div
      data-testid={`f5-t1a-key-${spec.key}`}
      data-f5-frozen={frozen ? '1' : '0'}
      className={cn(
        'flex items-center gap-4 flex-wrap min-h-[72px] px-6 py-4 border-t border-nav-hair',
        frozen ? 'bg-[#fbfcfc]' : 'bg-white',
      )}
    >
      <span className="flex-1 min-w-[220px]">
        <span className="flex items-center gap-2 flex-wrap">
          <span className="font-head text-[13.5px] font-bold">{t(labelKeyFor(spec.key))}</span>
          {spec.payroll && (
            <span className="bg-nav-redTint text-nav-red border border-[#e3b5ae] rounded-full px-2 py-0.5 font-head text-[9.5px] font-bold tracking-[0.06em] uppercase">
              {t('f5t1a.tag.payroll')}
            </span>
          )}
          {spec.billing && (
            <span className="bg-nav-redTint text-nav-red border border-[#e3b5ae] rounded-full px-2 py-0.5 font-head text-[9.5px] font-bold tracking-[0.06em] uppercase">
              {t('f5t1a.tag.billing')}
            </span>
          )}
          {frozen && (
            <span className="bg-nav-hair text-nav-slate border border-nav-card rounded-full px-2 py-0.5 font-head text-[9.5px] font-bold tracking-[0.06em] uppercase">
              {t('f5t1a.tag.frozen')}
            </span>
          )}
        </span>
        <span className="block mt-1 text-[12.5px] leading-snug text-nav-slate">
          {t(descKeyFor(spec.key))}
        </span>
        {spec.key === UNIQUE_DAILY_KEY && (
          <span data-testid="f5-t1a-daily-folded" className="block mt-1 text-[12px] text-nav-slate">
            {t('f5t1a.dailyFolded')}
          </span>
        )}
        {dailyDiverge && (
          <span className="block mt-1 text-[12px] text-nav-amberText">{t('f5t1a.dailyDiverge')}</span>
        )}
      </span>
      <span className="flex items-center gap-3 flex-none">
        <span
          data-testid={spec.key === UNIQUE_DAILY_KEY ? 'f5-t1a-daily-target-value' : undefined}
          className="inline-flex items-center min-h-[44px] px-3.5 bg-nav-soft border-[1.5px] border-nav-card rounded-lg font-head text-[13.5px] font-bold tabular-nums whitespace-nowrap"
        >
          {item ? formatValue(item.value, spec.field, t) : '—'}
        </span>
        {frozen ? (
          <span
            data-testid={`f5-t1a-nobody-${spec.key}`}
            className="inline-flex items-center gap-1.5 min-h-[44px] px-3.5 bg-nav-hair border-[1.5px] border-nav-card rounded-lg font-head text-[12.5px] font-bold text-nav-slate whitespace-nowrap"
          >
            {t('f5t1a.nobodyEdits')}
          </span>
        ) : (
          <>
            {onHistory && (
              <button
                type="button"
                onClick={onHistory}
                className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] px-3 border-[1.5px] border-nav-line rounded-lg bg-white font-head text-[13px] font-bold text-nav-navy hover:border-nav-teal hover:text-nav-tealDark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2"
              >
                <History className="w-3.5 h-3.5" aria-hidden />
                <span className="sr-only">{t('systemConfig.row.history')}</span>
              </button>
            )}
            <button
              type="button"
              onClick={onEdit}
              className="inline-flex items-center min-h-[44px] px-4 border-[1.5px] border-nav-line rounded-lg bg-white font-head text-[13px] font-bold text-nav-navy hover:bg-[#f4fafa] hover:border-nav-teal hover:text-nav-tealDark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2"
            >
              {t('f5t1a.edit')}
            </button>
          </>
        )}
      </span>
    </div>
  );
}

function FrozenFactRow({
  factId,
  value,
  t,
}: {
  factId: string;
  value: string;
  t: ReturnType<typeof useT>;
}): JSX.Element {
  return (
    <div
      data-testid={`f5-t1a-fact-${factId}`}
      data-f5-frozen="1"
      className="flex items-center gap-4 flex-wrap min-h-[72px] px-6 py-4 border-t border-nav-hair bg-[#fbfcfc]"
    >
      <span className="flex-1 min-w-[220px]">
        <span className="flex items-center gap-2 flex-wrap">
          <span className="font-head text-[13.5px] font-bold">{t(`f5t1a.fact.${factId}` as StringKey)}</span>
          <span className="bg-nav-hair text-nav-slate border border-nav-card rounded-full px-2 py-0.5 font-head text-[9.5px] font-bold tracking-[0.06em] uppercase">
            {t('f5t1a.tag.frozen')}
          </span>
        </span>
        <span className="block mt-1 text-[12.5px] leading-snug text-nav-slate">
          {t(`f5t1a.fact.${factId}.desc` as StringKey)}
        </span>
      </span>
      <span className="flex items-center gap-3 flex-none">
        <span className="inline-flex items-center min-h-[44px] px-3.5 bg-nav-soft border-[1.5px] border-nav-card rounded-lg font-head text-[13.5px] font-bold tabular-nums whitespace-nowrap">
          {value}
        </span>
        <span className="inline-flex items-center gap-1.5 min-h-[44px] px-3.5 bg-nav-hair border-[1.5px] border-nav-card rounded-lg font-head text-[12.5px] font-bold text-nav-slate whitespace-nowrap">
          {t('f5t1a.nobodyEdits')}
        </span>
      </span>
    </div>
  );
}

function formatValue(value: unknown, field: FieldKind, t: ReturnType<typeof useT>): string {
  if (value == null || value === '') return '—';
  if (field.kind === 'int') {
    const n = typeof value === 'number' ? value : Number(value);
    return `${Number.isFinite(n) ? n : String(value)} ${t(UNIT_I18N[field.unit])}`;
  }
  if (field.kind === 'bool') {
    return value === true || value === 'true' ? t('f5t1a.yes') : t('f5t1a.no');
  }
  if (field.kind === 'url' || field.kind === 'string') {
    const s = String(value).replace(/^"|"$/g, '');
    return s || '—';
  }
  if (field.kind === 'userId') return `#${value}`;
  if (field.kind === 'roleArray') {
    const arr = Array.isArray(value) ? value : [];
    return arr.length === 0 ? t('f5t1a.emptyRoles') : arr.join(', ');
  }
  if (field.kind === 'intArray') {
    const arr = Array.isArray(value) ? value : [];
    return arr.length === 0 ? '—' : arr.join(', ');
  }
  if (field.kind === 'stringArray') {
    const arr = Array.isArray(value) ? value : [];
    return arr.length === 0 ? '—' : String(arr.length);
  }
  if (field.kind === 'weeklyRhythm') {
    const v = (value ?? {}) as { bronze?: number; silver?: number; gold?: number };
    return `${v.bronze ?? '—'} · ${v.silver ?? '—'} · ${v.gold ?? '—'}`;
  }
  if (field.kind === 'approvalPct') {
    const v = (value ?? {}) as { bronze?: number; silver?: number; gold?: number };
    return `${v.bronze ?? '—'}% · ${v.silver ?? '—'}% · ${v.gold ?? '—'}%`;
  }
  if (field.kind === 'monthlyLadder') {
    const v = (value ?? {}) as { base?: number; t1?: number; t2?: number; t3?: number };
    return `${v.base ?? '—'} / ${v.t1 ?? '—'} / ${v.t2 ?? '—'} / ${v.t3 ?? '—'}`;
  }
  if (field.kind === 'timeBands') {
    return t('f5t1a.timeBands.short');
  }
  return String(value);
}

function SimpleEditPanel({
  spec,
  item,
  onClose,
}: {
  spec: CatalogKey;
  item: SystemConfigItem;
  onClose: () => void;
}): JSX.Element {
  const t = useT();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<unknown>(item.value);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const mut = useMutation({
    mutationFn: (value: unknown) => systemConfigApi.patch(spec.key, { value }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'system-config'] });
      toast.success(t('admin.settings.saved'), t('admin.settings.savedDesc'));
      onClose();
    },
    onError: (err) => {
      const message =
        err instanceof ApiError
          ? typeof err.payload?.message === 'string'
            ? err.payload.message
            : Array.isArray(err.payload?.message)
              ? (err.payload.message as string[]).join('; ')
              : err.message
          : (err as Error).message;
      setError(message);
    },
  });

  const onSave = (): void => {
    setError(null);
    try {
      mut.mutate(coerceDraft(spec.field, draft, t));
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-16 px-4">
      <button
        type="button"
        className="absolute inset-0 bg-[rgba(18,43,68,.42)]"
        aria-label={t('f5t1a.cancel')}
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="f5-t1a-edit-title"
        className="relative w-[580px] max-w-[calc(100%-32px)] bg-white border-[1.5px] border-nav-card rounded-[14px] shadow-[0_24px_60px_rgba(18,43,68,.28)] p-7 font-body text-nav-navy"
      >
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="min-w-0">
            <p id="f5-t1a-edit-title" className="m-0 mb-1 font-head text-[19px] font-bold tracking-[-0.01em]">
              {t(labelKeyFor(spec.key))}
            </p>
            <p className="m-0 text-[13px] text-nav-slate">{t(descKeyFor(spec.key))}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('f5t1a.cancel')}
            className="flex-none inline-flex items-center justify-center w-11 h-11 rounded-lg text-nav-slate hover:bg-nav-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
          >
            <X className="w-[18px] h-[18px]" />
          </button>
        </div>

        <label className="block mb-4">
          <span className="block font-head text-[10.5px] font-bold tracking-[0.12em] uppercase text-nav-slate">
            {t('f5t1a.currentValue')}
          </span>
          <div className="flex items-center h-11 mt-1.5 px-3 bg-nav-soft border-[1.5px] border-nav-card rounded-lg font-head text-[14px] font-bold text-nav-slate tabular-nums">
            {formatValue(item.value, spec.field, t)}
          </div>
        </label>

        <EditFields field={spec.field} draft={draft} setDraft={setDraft} t={t} />

        {error && <p className="text-xs text-nav-red mt-2">{error}</p>}

        <div className="flex items-center justify-end gap-2.5 mt-6 flex-wrap">
          <button
            type="button"
            onClick={onClose}
            className="min-h-[44px] px-[18px] border-[1.5px] border-nav-line rounded-lg bg-white font-head text-[13.5px] font-bold text-nav-navy hover:bg-[#f2f5f7] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
          >
            {t('f5t1a.cancel')}
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={mut.isPending}
            className="min-h-[44px] px-5 bg-nav-navy text-white border-[1.5px] border-nav-navy rounded-lg font-head text-[13.5px] font-bold hover:bg-nav-navyHover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal disabled:opacity-60"
          >
            {mut.isPending ? t('admin.settings.saving') : t('f5t1a.save')}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditFields({
  field,
  draft,
  setDraft,
  t,
}: {
  field: FieldKind;
  draft: unknown;
  setDraft: (v: unknown) => void;
  t: ReturnType<typeof useT>;
}): JSX.Element {
  const inp =
    'w-full box-border h-11 mt-1.5 px-3 border-[1.5px] border-nav-line rounded-lg font-body text-[14px] text-nav-navy bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal';

  if (field.kind === 'int') {
    return (
      <label className="block">
        <span className="block font-head text-[10.5px] font-bold tracking-[0.12em] uppercase text-nav-slate">
          {t('f5t1a.newValue')}
        </span>
        <div className="flex items-center gap-2">
          <input
            className={inp}
            type="number"
            value={draft == null ? '' : String(draft)}
            onChange={(e) => setDraft(e.target.value)}
            aria-label={t('f5t1a.newValue')}
          />
          <span className="flex-none mt-1.5 font-head text-[12.5px] font-bold text-nav-slate whitespace-nowrap">
            {t(UNIT_I18N[field.unit])}
          </span>
        </div>
      </label>
    );
  }
  if (field.kind === 'string' || field.kind === 'url') {
    return (
      <label className="block">
        <span className="block font-head text-[10.5px] font-bold tracking-[0.12em] uppercase text-nav-slate">
          {t('f5t1a.newValue')}
        </span>
        <input
          className={inp}
          type={field.kind === 'url' ? 'url' : 'text'}
          value={String(draft ?? '').replace(/^"|"$/g, '')}
          onChange={(e) => setDraft(e.target.value)}
          aria-label={t('f5t1a.newValue')}
        />
      </label>
    );
  }
  if (field.kind === 'bool') {
    const on = draft === true || draft === 'true';
    return (
      <button
        type="button"
        role="switch"
        aria-checked={on}
        onClick={() => setDraft(!on)}
        className={cn(
          'inline-flex items-center min-h-[44px] px-4 rounded-lg border-[1.5px] font-head text-[13.5px] font-bold',
          on ? 'bg-nav-navy text-white border-nav-navy' : 'bg-white text-nav-navy border-nav-line',
        )}
      >
        {on ? t('f5t1a.yes') : t('f5t1a.no')}
      </button>
    );
  }
  if (field.kind === 'userId') {
    return <UserIdField value={String(draft ?? '')} onChange={setDraft} t={t} />;
  }
  if (field.kind === 'intArray') {
    const arr = Array.isArray(draft) ? (draft as number[]) : [];
    return (
      <label className="block">
        <span className="block font-head text-[10.5px] font-bold tracking-[0.12em] uppercase text-nav-slate">
          {t('f5t1a.intArrayHint')}
        </span>
        <input
          className={inp}
          value={arr.join(', ')}
          onChange={(e) =>
            setDraft(
              e.target.value
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean)
                .map((s) => Number(s)),
            )
          }
        />
      </label>
    );
  }
  if (field.kind === 'stringArray') {
    const arr = Array.isArray(draft) ? (draft as string[]) : [];
    return (
      <label className="block">
        <span className="block font-head text-[10.5px] font-bold tracking-[0.12em] uppercase text-nav-slate">
          {t('f5t1a.stringArrayHint')}
        </span>
        <input
          className={inp}
          value={arr.join(', ')}
          onChange={(e) =>
            setDraft(
              e.target.value
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean),
            )
          }
        />
      </label>
    );
  }
  if (field.kind === 'weeklyRhythm' || field.kind === 'approvalPct') {
    const v = (draft ?? {}) as { bronze?: number; silver?: number; gold?: number };
    const set = (k: 'bronze' | 'silver' | 'gold', raw: string) =>
      setDraft({ ...v, [k]: raw === '' ? '' : Number(raw) });
    const labels: Array<['bronze' | 'silver' | 'gold', StringKey]> = [
      ['bronze', 'f5t1a.rhythm.step1'],
      ['silver', 'f5t1a.rhythm.step2'],
      ['gold', 'f5t1a.rhythm.step3'],
    ];
    return (
      <div className="grid grid-cols-3 gap-2" data-testid="f5-t1a-rhythm-fields">
        {labels.map(([k, lk]) => (
          <label key={k} className="block">
            <span className="block font-head text-[10.5px] font-bold tracking-[0.12em] uppercase text-nav-slate">
              {t(lk)}
            </span>
            <input
              className={inp}
              type="number"
              value={v[k] ?? ''}
              onChange={(e) => set(k, e.target.value)}
            />
          </label>
        ))}
      </div>
    );
  }
  if (field.kind === 'monthlyLadder') {
    const v = (draft ?? {}) as { base?: number; t1?: number; t2?: number; t3?: number };
    const set = (k: 'base' | 't1' | 't2' | 't3', raw: string) =>
      setDraft({ ...v, [k]: raw === '' ? '' : Number(raw) });
    const labels: Array<['base' | 't1' | 't2' | 't3', StringKey]> = [
      ['base', 'f5t1a.metal.bronce'],
      ['t1', 'f5t1a.metal.plata'],
      ['t2', 'f5t1a.metal.oro'],
      ['t3', 'f5t1a.metal.platino'],
    ];
    return (
      <div className="grid grid-cols-2 gap-2" data-testid="f5-t1a-ladder-fields">
        {labels.map(([k, lk]) => (
          <label key={k} className="block">
            <span className="block font-head text-[10.5px] font-bold tracking-[0.12em] uppercase text-nav-slate">
              {t(lk)}
            </span>
            <input className={inp} type="number" value={v[k] ?? ''} onChange={(e) => set(k, e.target.value)} />
          </label>
        ))}
      </div>
    );
  }
  if (field.kind === 'timeBands') {
    const v = (draft ?? {}) as Record<string, [number, number]>;
    const bands = ['manana', 'tarde', 'noche'] as const;
    return (
      <div className="grid gap-2">
        {bands.map((b) => (
          <div key={b} className="flex items-center gap-2">
            <span className="w-20 font-head text-[12px] font-bold">{t(`f5t1a.band.${b}` as StringKey)}</span>
            <input
              className={inp}
              type="number"
              value={v[b]?.[0] ?? ''}
              onChange={(e) =>
                setDraft({ ...v, [b]: [Number(e.target.value), v[b]?.[1] ?? 0] })
              }
            />
            <input
              className={inp}
              type="number"
              value={v[b]?.[1] ?? ''}
              onChange={(e) =>
                setDraft({ ...v, [b]: [v[b]?.[0] ?? 0, Number(e.target.value)] })
              }
            />
          </div>
        ))}
      </div>
    );
  }
  return <p className="text-sm text-nav-slate">{t('f5t1a.unsupported')}</p>;
}

function UserIdField({
  value,
  onChange,
  t,
}: {
  value: string;
  onChange: (v: unknown) => void;
  t: ReturnType<typeof useT>;
}): JSX.Element {
  const q = useQuery({
    queryKey: ['admin', 'users', 'admin-active'],
    queryFn: () => usersApi.list({ role: 'admin', active: true }, 100, 0),
    staleTime: 60_000,
  });
  return (
    <label className="block">
      <span className="block font-head text-[10.5px] font-bold tracking-[0.12em] uppercase text-nav-slate">
        {t('f5t1a.newValue')}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full h-11 mt-1.5 px-3 border-[1.5px] border-nav-line rounded-lg font-body text-[14px] bg-white"
      >
        <option value="">—</option>
        {(q.data?.items ?? []).map((u) => (
          <option key={u.id} value={u.id}>
            {u.fullName} ({u.email})
          </option>
        ))}
      </select>
    </label>
  );
}

function coerceDraft(field: FieldKind, draft: unknown, t: ReturnType<typeof useT>): unknown {
  if (field.kind === 'int') {
    const n = typeof draft === 'number' ? draft : Number(String(draft).trim());
    if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) {
      throw new Error(t('admin.settings.invalidNumber'));
    }
    return n;
  }
  if (field.kind === 'url') {
    const s = String(draft ?? '').trim();
    if (s !== '' && !/^https:\/\//i.test(s)) throw new Error(t('admin.nav.settings.badUrl'));
    return s;
  }
  if (field.kind === 'string') return String(draft ?? '');
  if (field.kind === 'bool') return draft === true || draft === 'true';
  if (field.kind === 'userId') {
    const n = Number(draft);
    if (!Number.isFinite(n) || n < 1) throw new Error(t('admin.settings.invalidNumber'));
    return n;
  }
  if (field.kind === 'intArray') {
    const arr = Array.isArray(draft) ? draft : [];
    if (arr.some((x) => typeof x !== 'number' || !Number.isFinite(x))) {
      throw new Error(t('admin.settings.invalidNumber'));
    }
    return arr;
  }
  if (field.kind === 'stringArray') {
    return Array.isArray(draft) ? draft.filter((x) => typeof x === 'string' && x.length > 0) : [];
  }
  if (field.kind === 'weeklyRhythm' || field.kind === 'approvalPct') {
    const v = draft as { bronze?: number; silver?: number; gold?: number };
    for (const k of ['bronze', 'silver', 'gold'] as const) {
      if (typeof v[k] !== 'number' || !Number.isFinite(v[k])) throw new Error(t('admin.settings.invalidNumber'));
    }
    return { bronze: v.bronze, silver: v.silver, gold: v.gold };
  }
  if (field.kind === 'monthlyLadder') {
    const v = draft as { base?: number; t1?: number; t2?: number; t3?: number };
    for (const k of ['base', 't1', 't2', 't3'] as const) {
      if (typeof v[k] !== 'number' || !Number.isInteger(v[k]) || v[k]! < 0) {
        throw new Error(t('admin.settings.invalidNumber'));
      }
    }
    if (!(v.base! < v.t1! && v.t1! < v.t2! && v.t2! < v.t3!)) {
      throw new Error(t('systemConfig.edit.tier.monotoneError'));
    }
    return { base: v.base, t1: v.t1, t2: v.t2, t3: v.t3 };
  }
  if (field.kind === 'timeBands') return draft;
  throw new Error(t('f5t1a.unsupported'));
}
